import { Injectable } from '@angular/core';
import { Web3Service } from './web3.service';
import { StateService } from './state.service';
import { environment } from 'src/environments/environment';
import { waitForTransactionReceipt } from '@wagmi/core';
import { firstValueFrom, forkJoin } from 'rxjs';

import CryptoPunksMarketABI from '../../abi/CryptoPunksMarket.json';
import CryptoPunksV2WrappedTokenABI from '../../abi/CryptoPunksV2WrappedToken.json';
import C721ABI from '../../abi/C721.json';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root'
})
export class PunkWrapperService {

  // C721 contract address from environment
  private readonly C721_ADDRESS = environment.c721Address;

  constructor(
    private web3Service: Web3Service,
    private dataService: DataService,
    private stateService: StateService
  ) {
    console.log('[PunkWrapperService] Constructor called, Web3Service config status:', Boolean(this.web3Service.config));
  }

  // Helper method to get public client
  private getPublicClient() {
    return this.web3Service.getPublicClient();
  }

  // Helper method to get wallet client
  private async getWalletClient() {
    return await this.web3Service.getWalletClient();
  }

  // Helper method to get current user address
  private getCurrentUser(): string | null {
    return this.stateService.getWalletAddress();
  }

  // Helper method to validate address
  private isValidAddress(address: string): boolean {
    return Boolean(address && address.length === 42 && address.startsWith('0x'));
  }

  // Helper method to check if service is ready
  private isServiceReady(): boolean {
    return Boolean(this.web3Service.config);
  }

  // Public method to check if service is ready
  public isReady(): boolean {
    return this.isServiceReady();
  }

  // Public method to wait for service to be ready
  public async waitForReady(timeoutMs: number = 5000): Promise<boolean> {
    const startTime = Date.now();
    
    while (!this.isServiceReady() && (Date.now() - startTime) < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return this.isServiceReady();
  }

  // Public method to get service status for debugging
  public getServiceStatus(): { isReady: boolean; hasConfig: boolean; configType: string } {
    return {
      isReady: this.isServiceReady(),
      hasConfig: Boolean(this.web3Service.config),
      configType: this.web3Service.config ? 'initialized' : 'not initialized'
    };
  }

  // Helper method to ensure service is ready before operations
  private async ensureServiceReady(): Promise<void> {
    if (!this.isServiceReady()) {
      console.log('[PunkWrapperService] Service not ready, waiting for initialization...');
      const isReady = await this.waitForReady();
      if (!isReady) {
        console.error('[PunkWrapperService] Failed to initialize within timeout period');
        throw new Error('PunkWrapperService failed to initialize within timeout period');
      }
      console.log('[PunkWrapperService] Service is now ready');
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // LEGACY WRAPPER INTERFACE METHODS
  ////////////////////////////////////////////////////////////////////////////////

  async getProxyInfo(user: string): Promise<string | null> {
    try {
      await this.ensureServiceReady();
      
      const publicClient = this.getPublicClient();
      if (!publicClient) return null;

      const proxy = await publicClient.readContract({
        address: environment.wrappedPunksAddress as `0x${string}`,
        abi: CryptoPunksV2WrappedTokenABI,
        functionName: 'proxyInfo',
        args: [user as `0x${string}`],
      });
      console.log('[PunkWrapperService] proxy:', proxy);
      if (proxy === '0x0000000000000000000000000000000000000000') {
        return null;
      }

      return proxy as string;
    } catch (error) {
      console.error('Error getting proxy info:', error);
      return null;
    }
  }

  async registerProxy(user: string, chainId: number, onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      await this.ensureServiceReady();
      
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');

      const txHash = await walletClient.writeContract({
        address: environment.wrappedPunksAddress as `0x${string}`,
        abi: CryptoPunksV2WrappedTokenABI,
        functionName: 'registerProxy',
        args: [],
      });

      if (onSuccess) {
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHash });
        onSuccess(receipt);
      }

      return txHash;
    } catch (error) {
      console.error('Error registering proxy:', error);
      throw error;
    }
  }

  async mintWrappedPunk(user: string, chainId: number, punkIndex: number, onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      await this.ensureServiceReady();
      
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');

      const txHash = await walletClient.writeContract({
        address: environment.wrappedPunksAddress as `0x${string}`,
        abi: CryptoPunksV2WrappedTokenABI,
        functionName: 'mint',
        args: [punkIndex],
      });

      if (onSuccess) {
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHash });
        onSuccess(receipt);
      }

      return txHash;
    } catch (error) {
      console.error('Error minting wrapped punk:', error);
      throw error;
    }
  }

  async burnWrappedPunk(user: string, chainId: number, punkIndex: number, onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');

      const txHash = await walletClient.writeContract({
        address: environment.wrappedPunksAddress as `0x${string}`,
        abi: CryptoPunksV2WrappedTokenABI,
        functionName: 'burn',
        args: [punkIndex],
      });

      if (onSuccess) {
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHash });
        onSuccess(receipt);
      }

      return txHash;
    } catch (error) {
      console.error('Error burning wrapped punk:', error);
      throw error;
    }
  }

  async isPunkWrapped(user: string, chainId: number, punkIndex: number): Promise<boolean> {
    try {
      await this.ensureServiceReady();
      
      const publicClient = this.getPublicClient();
      if (!publicClient) return false;

      const owner = await publicClient.readContract({
        address: environment.wrappedPunksAddress as `0x${string}`,
        abi: CryptoPunksV2WrappedTokenABI,
        functionName: 'ownerOf',
        args: [punkIndex],
      });

      return (owner as string).toLowerCase() === user.toLowerCase();
    } catch (error) {
      console.error('Error checking if punk is wrapped:', error);
      return false;
    }
  }

  async isPunkC721Wrapped(user: string, chainId: number, punkIndex: number): Promise<boolean> {
    try {
      await this.ensureServiceReady();
      
      const publicClient = this.getPublicClient();
      if (!publicClient) return false;

      const owner = await publicClient.readContract({
        address: this.C721_ADDRESS as `0x${string}`,
        abi: C721ABI,
        functionName: 'ownerOf',
        args: [punkIndex],
      });

      return (owner as string).toLowerCase() === user.toLowerCase();
    } catch (error) {
      console.error('Error checking if punk is C721 wrapped:', error);
      return false;
    }
  }

  async getWrappedPunksOfOwner(user: string, chainId: number): Promise<number[]> {
    try {
      const publicClient = this.getPublicClient();
      if (!publicClient) return [];

      const balance = await publicClient.readContract({
        address: environment.wrappedPunksAddress as `0x${string}`,
        abi: CryptoPunksV2WrappedTokenABI,
        functionName: 'balanceOf',
        args: [user as `0x${string}`],
      });

      const count = Number(balance);
      if (count === 0) return [];

      const results = await publicClient.multicall({
        contracts: Array.from({ length: count }, (_, i) => ({
          address: environment.wrappedPunksAddress as `0x${string}`,
          abi: CryptoPunksV2WrappedTokenABI as any,
          functionName: 'tokenOfOwnerByIndex',
          args: [user as `0x${string}`, i],
        })),
        allowFailure: true,
      });

      return results
        .filter(r => r.status === 'success')
        .map(r => Number(r.result));
    } catch (error) {
      console.error('Error getting wrapped punks of owner:', error);
      return [];
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // CRYPTOPUNKS MARKET INTERFACE METHODS
  ////////////////////////////////////////////////////////////////////////////////

  async allPunksAssigned(user: string, chainId: number): Promise<boolean> {
    try {
      const publicClient = this.getPublicClient();
      if (!publicClient) return false;

      const allAssigned = await publicClient.readContract({
        address: environment.punksMarketAddress as `0x${string}`,
        abi: CryptoPunksMarketABI,
        functionName: 'allPunksAssigned',
        args: [],
      });

      return allAssigned as boolean;
    } catch (error) {
      console.error('Error checking if all punks assigned:', error);
      return false;
    }
  }

  async punkIndexToAddress(user: string, chainId: number, punkIndex: number): Promise<string> {
    try {
      const publicClient = this.getPublicClient();
      if (!publicClient) throw new Error('No public client');

      const owner = await publicClient.readContract({
        address: environment.punksMarketAddress as `0x${string}`,
        abi: CryptoPunksMarketABI,
        functionName: 'punkIndexToAddress',
        args: [punkIndex],
      });

      return owner as string;
    } catch (error) {
      console.error('Error getting punk owner:', error);
      throw error;
    }
  }

  async transferPunk(user: string, chainId: number, to: string, punkIndex: number, onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');

      const txHash = await walletClient.writeContract({
        address: environment.punksMarketAddress as `0x${string}`,
        abi: CryptoPunksMarketABI,
        functionName: 'transferPunk',
        args: [to as `0x${string}`, punkIndex],
      });

      if (onSuccess) {
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHash });
        onSuccess(receipt);
      }

      return txHash;
    } catch (error) {
      console.error('Error transferring punk:', error);
      throw error;
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // C721 INTERFACE METHODS
  ////////////////////////////////////////////////////////////////////////////////

  async getPunkProxyForUser(owner: string): Promise<string | null> {
    try {
      const publicClient = this.getPublicClient();
      if (!publicClient) {
        console.error('No public client');
        return '';
      }
      
      const proxy = await publicClient.readContract({
        address: this.C721_ADDRESS as `0x${string}`,
        abi: C721ABI,
        functionName: 'punkProxyForUser',
        args: [owner as `0x${string}`],
      });

      if (!proxy || proxy === '0x0000000000000000000000000000000000000000') {
        return null;
      }

      return proxy as string;
    } catch (error) {
      console.error('Error getting punk proxy for user:', error);
      return null
    }
  }

  async transferPunkToStash(user: string, chainId: number, punkIndex: number, onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');

      // First get the stash address
      const stashAddress = await this.getPunkProxyForUser(user);
      
      // Transfer punk to stash
      const txHash = await walletClient.writeContract({
        address: environment.punksMarketAddress as `0x${string}`,
        abi: CryptoPunksMarketABI,
        functionName: 'transferPunk',
        args: [stashAddress as `0x${string}`, punkIndex],
      });

      if (onSuccess) {
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHash });
        onSuccess(receipt);
      }

      return txHash;
    } catch (error) {
      console.error('Error transferring punk to stash:', error);
      throw error;
    }
  }

  async transferPunkBatchToStash(user: string, chainId: number, punkIndexes: number[], onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');

      // Get the stash address
      const stashAddress = await this.getPunkProxyForUser(user);
      
      // For batch transfer, we'll need to do multiple transactions
      // This is a simplified version - you might want to implement a more efficient batch method
      const txHashes: string[] = [];
      
      for (const punkIndex of punkIndexes) {
        const txHash = await walletClient.writeContract({
          address: environment.punksMarketAddress as `0x${string}`,
          abi: CryptoPunksMarketABI,
          functionName: 'transferPunk',
          args: [stashAddress as `0x${string}`, punkIndex],
        });
        txHashes.push(txHash);
      }

      if (onSuccess) {
        // Wait for the last transaction
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHashes[txHashes.length - 1] as `0x${string}` });
        onSuccess(receipt);
      }

      return txHashes[txHashes.length - 1];
    } catch (error) {
      console.error('Error transferring punks batch to stash:', error);
      throw error;
    }
  }

  async wrapPunk(user: string, chainId: number, punkIndex: number, onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');
      
      const txHash = await walletClient.writeContract({
        address: this.C721_ADDRESS as `0x${string}`,
        abi: C721ABI,
        functionName: 'wrapPunk',
        args: [punkIndex],
      });

      if (onSuccess) {
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHash });
        onSuccess(receipt);
      }

      return txHash;
    } catch (error) {
      console.error('Error wrapping punk:', error);
      throw error;
    }
  }

  async wrapPunkBatch(user: string, chainId: number, punkIndexes: number[], onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');
      
      const txHash = await walletClient.writeContract({
        address: this.C721_ADDRESS as `0x${string}`,
        abi: C721ABI,
        functionName: 'wrapPunkBatch',
        args: [punkIndexes],
      });

      if (onSuccess) {
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHash });
        onSuccess(receipt);
      }

      return txHash;
    } catch (error) {
      console.error('Error wrapping punks batch:', error);
      throw error;
    }
  }

  async unwrapPunk(user: string, chainId: number, punkIndex: number, onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');
      
      const txHash = await walletClient.writeContract({
        address: this.C721_ADDRESS as `0x${string}`,
        abi: C721ABI,
        functionName: 'unwrapPunk',
        args: [punkIndex],
      });

      if (onSuccess) {
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHash });
        onSuccess(receipt);
      }

      return txHash;
    } catch (error) {
      console.error('Error unwrapping punk:', error);
      throw error;
    }
  }

  async unwrapPunkBatch(user: string, chainId: number, punkIndexes: number[], onSuccess?: (tx: any) => void): Promise<string | undefined> {
    try {
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');
      
      const txHash = await walletClient.writeContract({
        address: this.C721_ADDRESS as `0x${string}`,
        abi: C721ABI,
        functionName: 'unwrapPunkBatch',
        args: [punkIndexes],
      });

      if (onSuccess) {
        const receipt = await waitForTransactionReceipt(this.web3Service.config, { hash: txHash });
        onSuccess(receipt);
      }

      return txHash;
    } catch (error) {
      console.error('Error unwrapping punks batch:', error);
      throw error;
    }
  }

  async getTokensOfOwner(user: string, chainId: number, owner: string): Promise<number[]> {
    try {
      const publicClient = this.getPublicClient();
      if (!publicClient) return [];
      
      const tokens = await publicClient.readContract({
        address: this.C721_ADDRESS as `0x${string}`,
        abi: C721ABI,
        functionName: 'tokensOfOwner',
        args: [owner as `0x${string}`],
      });

      return (tokens as bigint[]).map(token => Number(token));
    } catch (error) {
      console.error('Error getting tokens of owner:', error);
      return [];
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // UTILITY METHODS
  ////////////////////////////////////////////////////////////////////////////////

  getChainId(): number {
    return environment.production ? 1 : 31337;
  }

  isValidProxy(proxy: string): boolean {
    return this.isValidAddress(proxy);
  }

  ////////////////////////////////////////////////////////////////////////////////
  // DEV SETUP METHODS (for local development)
  ////////////////////////////////////////////////////////////////////////////////

  async initializeAllPunks(user: string, chainId: number): Promise<void> {
    try {
      const walletClient = await this.getWalletClient();
      if (!walletClient) throw new Error('No wallet client');

      // This would initialize all punks for the user
      // Implementation depends on your specific requirements
      console.log('Initializing all punks for user:', user);

      
    } catch (error) {
      console.error('Error initializing all punks:', error);
      throw error;
    }
  }

  async queryPunksOwnersLocal(user: string, chainId: number, punkIds: number[]): Promise<number[]> {
    try {
      const publicClient = this.getPublicClient();
      if (!publicClient || punkIds.length === 0) return [];

      const results = await publicClient.multicall({
        contracts: punkIds.map(punkId => ({
          address: environment.punksMarketAddress as `0x${string}`,
          abi: CryptoPunksMarketABI as any,
          functionName: 'punkIndexToAddress',
          args: [punkId],
        })),
        allowFailure: true,
      });

      const userLower = user.toLowerCase();
      return punkIds.filter((_, i) => {
        const r = results[i];
        return r.status === 'success' && (r.result as string).toLowerCase() === userLower;
      });
    } catch (error) {
      console.error('Error querying punks owners locally:', error);
      return [];
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // SUBGRAPH METHODS (for mainnet)
  ////////////////////////////////////////////////////////////////////////////////

  async queryPunksFromSubgraph(user: string, proxy: string, c721Proxy: string): Promise<any> {
    const empty = { myPunks: [], myProxyPunks: [], myStashPunks: [], myWrappedPunks: [], myC721Tokens: [], allPunks: [] };
    try {
      const hasProxy = proxy && this.isValidProxy(proxy);
      const hasC721Proxy = c721Proxy && this.isValidProxy(c721Proxy);

      const collections = [
        environment.punksMarketAddress,
        environment.wrappedPunksAddress,
        environment.c721Address,
      ].filter(Boolean);

      const [userTokens, proxyTokens, stashTokens] = await firstValueFrom(forkJoin([
        this.dataService.fetchOwnedIds(user, collections),
        hasProxy
          ? this.dataService.fetchOwnedIds(proxy.toLowerCase(), collections)
          : Promise.resolve([]),
        hasC721Proxy
          ? this.dataService.fetchOwnedIds(c721Proxy.toLowerCase(), collections)
          : Promise.resolve([]),
      ]));

      const myPunks: number[] = [];
      const myWrappedPunks: number[] = [];
      const myC721Tokens: number[] = [];

      for (const t of userTokens) {
        const id = Number(t.tokenId);
        switch (t.wrappingStatus) {
          case 'c721wrapped': myC721Tokens.push(id); break;
          case 'wrapped':     myWrappedPunks.push(id); break;
          default:            myPunks.push(id); break;
        }
      }

      const myProxyPunks = proxyTokens.map(t => Number(t.tokenId));

      const c721Set = new Set(myC721Tokens);
      const myStashPunks = stashTokens
        .map(t => Number(t.tokenId))
        .filter(id => !c721Set.has(id));

      const allPunks = [...myPunks, ...myWrappedPunks, ...myProxyPunks, ...myStashPunks, ...myC721Tokens];

      console.log(`[PunkWrapperService] Loaded ${allPunks.length} punks from API`);

      return { myPunks, myProxyPunks, myStashPunks, myWrappedPunks, myC721Tokens, allPunks };

    } catch (error) {
      console.error('[PunkWrapperService] Error in queryPunksFromSubgraph:', error);
      return empty;
    }
  }

} 